<template>
  <transition name="fade-scale">
    <div
      v-if="isVisible"
      class="workflow-guide-modal"
      :class="{ 'dark-mode': isDarkMode }"
      @click.self="close"
    >
      <div class="workflow-guide-container" :class="viewMode">
        <!-- Header -->
        <div class="workflow-header">
          <div class="header-left">
            <c-icon :name="getWorkflowIcon()" class="workflow-icon" />
            <div class="header-text">
              <h2 class="workflow-title">{{ currentWorkflow?.name || 'Workflow Guide' }}</h2>
              <span v-if="currentWorkflow" class="workflow-meta">
                <c-badge :color="currentWorkflow.type_badge_color">{{ formatType(currentWorkflow.workflow_type) }}</c-badge>
                <c-badge :color="currentWorkflow.difficulty_badge_color">{{ currentWorkflow.difficulty_level }}</c-badge>
                <span class="time-estimate">
                  <c-icon name="cilClock" /> {{ currentWorkflow.estimated_time }}
                </span>
              </span>
            </div>
          </div>
          <div class="header-actions">
            <button
              v-if="hasAlternatives"
              class="btn-alternatives"
              @click="showAlternatives = !showAlternatives"
              title="Show alternative workflows"
            >
              <c-icon name="cilSwapHorizontal" />
              {{ showAlternatives ? 'Hide' : 'Show' }} Alternatives
            </button>
            <button class="btn-close" @click="close" aria-label="Close">
              <c-icon name="cilX" />
            </button>
          </div>
        </div>

        <!-- View Mode Switcher -->
        <div v-if="currentWorkflow" class="view-mode-switcher">
          <button
            :class="{ active: viewMode === 'guided' }"
            @click="viewMode = 'guided'"
          >
            <c-icon name="cilListNumbered" /> Guided Mode
          </button>
          <button
            :class="{ active: viewMode === 'overview' }"
            @click="viewMode = 'overview'"
          >
            <c-icon name="cilList" /> Overview
          </button>
        </div>

        <!-- Content Area -->
        <div class="workflow-content" ref="contentArea">
          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>Loading workflow...</p>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="error-state">
            <c-icon name="cilWarning" class="error-icon" />
            <h3>Failed to Load Workflow</h3>
            <p>{{ error }}</p>
            <button class="btn-retry" @click="retry">
              <c-icon name="cilReload" /> Retry
            </button>
          </div>

          <!-- Workflow List (initial state) -->
          <div v-else-if="!currentWorkflow && workflows.length > 0" class="workflow-list">
            <h3>Suggested Workflows</h3>
            <div class="workflow-grid">
              <div
                v-for="workflow in workflows"
                :key="workflow.id"
                class="workflow-card"
                @click="loadWorkflow(workflow.id)"
              >
                <div class="card-header">
                  <c-icon :name="getWorkflowIcon(workflow.workflow_type)" />
                  <h4>{{ workflow.name }}</h4>
                </div>
                <p class="card-description">{{ workflow.description }}</p>
                <div class="card-meta">
                  <span class="steps-count">
                    <c-icon name="cilListNumbered" /> {{ workflow.total_steps }} steps
                  </span>
                  <span class="time-estimate">
                    <c-icon name="cilClock" /> {{ workflow.estimated_time }}
                  </span>
                </div>
                <div class="card-badges">
                  <c-badge :color="workflow.type_badge_color">{{ formatType(workflow.workflow_type) }}</c-badge>
                  <c-badge :color="workflow.difficulty_badge_color">{{ workflow.difficulty_level }}</c-badge>
                </div>
                <div v-if="workflow.completion_rate > 0" class="card-stats">
                  <div class="stat">
                    <c-icon name="cilCheckCircle" />
                    {{ workflow.completion_rate }}% completion
                  </div>
                  <div class="stat" v-if="workflow.average_rating > 0">
                    <c-icon name="cilStar" />
                    {{ workflow.average_rating }}/5
                  </div>
                </div>
              </div>
            </div>

            <!-- Alternatives Section -->
            <div v-if="alternatives.length > 0" class="alternatives-section">
              <h4>Alternative Approaches</h4>
              <div class="alternatives-list">
                <div
                  v-for="alt in alternatives"
                  :key="alt.id"
                  class="alternative-item"
                  @click="loadWorkflow(alt.id)"
                >
                  <c-icon name="cilSwapHorizontal" />
                  <span>{{ alt.name }}</span>
                  <c-badge :color="alt.type_badge_color">{{ formatType(alt.workflow_type) }}</c-badge>
                </div>
              </div>
            </div>

            <!-- Workarounds Section -->
            <div v-if="workarounds.length > 0" class="workarounds-section">
              <h4>
                <c-icon name="cilInfo" /> Common Workarounds
              </h4>
              <div class="workarounds-list">
                <div
                  v-for="workaround in workarounds"
                  :key="workaround.id"
                  class="workaround-item"
                  @click="showWorkaround(workaround)"
                >
                  <div class="workaround-header">
                    <c-badge :color="workaround.category_badge_color">{{ workaround.problem_category }}</c-badge>
                    <h5>{{ workaround.problem_title }}</h5>
                  </div>
                  <p>{{ workaround.workaround_title }}</p>
                  <span v-if="workaround.is_temporary" class="temp-badge">
                    <c-icon name="cilClock" /> Temporary Solution
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Guided Mode View -->
          <div v-else-if="currentWorkflow && viewMode === 'guided'" class="guided-view">
            <!-- Prerequisites -->
            <div v-if="currentStep === -1" class="prerequisites-screen">
              <!-- Welcome Banner -->
              <div class="prerequisites-banner">
                <div class="banner-icon">
                  <c-icon name="cilRocket" />
                </div>
                <div class="banner-text">
                  <h3>Ready to Get Started?</h3>
                  <p>Let's make sure you have everything you need for a smooth workflow.</p>
                </div>
              </div>

              <!-- Prerequisites Section -->
              <div v-if="currentWorkflow.prerequisites && currentWorkflow.prerequisites.length > 0" class="prerequisites-section">
                <div class="section-header">
                  <c-icon name="cilClipboard" />
                  <h4>Before You Start</h4>
                </div>
                <div class="prerequisites-list">
                  <div
                    v-for="(prereq, index) in currentWorkflow.prerequisites"
                    :key="index"
                    class="prerequisite-item"
                  >
                    <div class="prereq-checkbox">
                      <c-icon name="cilCheckCircle" />
                    </div>
                    <span class="prereq-text">{{ prereq }}</span>
                  </div>
                </div>
              </div>

              <!-- Expected Outcome Section -->
              <div v-if="currentWorkflow.expected_outcome && currentWorkflow.expected_outcome.length > 0" class="expected-outcome">
                <div class="section-header">
                  <c-icon name="cilStar" />
                  <h4>What You'll Achieve</h4>
                </div>
                <ul class="outcomes-list">
                  <li v-for="(outcome, index) in currentWorkflow.expected_outcome" :key="index" class="outcome-item">
                    <c-icon name="cilCheckAlt" class="outcome-icon" />
                    <span>{{ outcome }}</span>
                  </li>
                </ul>
              </div>

              <!-- Workflow Stats -->
              <div class="workflow-stats">
                <div class="stat-card">
                  <c-icon name="cilClock" />
                  <div class="stat-content">
                    <span class="stat-label">Estimated Time</span>
                    <span class="stat-value">{{ currentWorkflow.estimated_time }}</span>
                  </div>
                </div>
                <div class="stat-card">
                  <c-icon name="cilLayers" />
                  <div class="stat-content">
                    <span class="stat-label">Total Steps</span>
                    <span class="stat-value">{{ currentWorkflow.total_steps }}</span>
                  </div>
                </div>
                <div class="stat-card">
                  <c-icon name="cilGauge" />
                  <div class="stat-content">
                    <span class="stat-label">Difficulty</span>
                    <span class="stat-value">{{ currentWorkflow.difficulty_level }}</span>
                  </div>
                </div>
              </div>

              <!-- Start Button -->
              <div class="start-button-container">
                <button class="btn-start-workflow" @click="startWorkflow">
                  <c-icon name="cilMediaPlay" /> 
                  <span>Start Workflow</span>
                  <c-icon name="cilArrowRight" />
                </button>
              </div>
            </div>

            <!-- Steps -->
            <div v-else-if="currentStep >= 0" class="step-view">
              <div class="step-progress">
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
                </div>
                <span class="progress-text">
                  Step {{ currentStep + 1 }} of {{ currentWorkflow.total_steps }}
                  ({{ Math.round(progressPercentage) }}%)
                </span>
              </div>

              <div class="step-content">
                <div class="step-header">
                  <span class="step-number">{{ currentStep + 1 }}</span>
                  <h3>{{ currentStepData.title }}</h3>
                </div>
                <div class="step-description" v-html="formatDescription(currentStepData.description)"></div>
                
                <!-- Step Action -->
                <div v-if="currentStepData.action" class="step-action">
                  <button
                    v-if="currentStepData.action.type === 'navigate'"
                    class="btn-action"
                    @click="executeAction(currentStepData.action)"
                  >
                    <c-icon :name="currentStepData.action.icon || 'cilArrowRight'" />
                    {{ currentStepData.action.label || 'Go to Page' }}
                  </button>
                  <button
                    v-else-if="currentStepData.action.type === 'execute'"
                    class="btn-action"
                    @click="executeAction(currentStepData.action)"
                  >
                    <c-icon :name="currentStepData.action.icon || 'cilCheckCircle'" />
                    {{ currentStepData.action.label || 'Execute' }}
                  </button>
                </div>

                <!-- Step Notes -->
                <div v-if="currentStepData.notes" class="step-notes">
                  <c-icon name="cilLightbulb" />
                  <p>{{ currentStepData.notes }}</p>
                </div>
              </div>

              <!-- Step Navigation -->
              <div class="step-navigation">
                <button
                  class="btn-prev"
                  @click="previousStep"
                  :disabled="currentStep === 0"
                >
                  <c-icon name="cilChevronLeft" /> Previous
                </button>
                <button class="btn-skip" @click="skipStep">
                  Skip Step
                </button>
                <button
                  class="btn-next"
                  @click="nextStep"
                  :disabled="currentStep >= currentWorkflow.total_steps - 1"
                >
                  {{ currentStep === currentWorkflow.total_steps - 1 ? 'Complete' : 'Next' }}
                  <c-icon name="cilChevronRight" />
                </button>
              </div>

              <!-- Help Button -->
              <button class="btn-need-help" @click="openAIAssistant">
                <c-icon name="cilLifeRing" /> Need Help?
              </button>
            </div>

            <!-- Completion Screen -->
            <div v-else-if="currentStep === currentWorkflow.total_steps" class="completion-screen">
              <div class="completion-icon">
                <c-icon name="cilCheckCircle" />
              </div>
              <h2>Workflow Completed!</h2>
              <p>Great job! You've successfully completed all {{ currentWorkflow.total_steps }} steps.</p>
              
              <!-- Feedback -->
              <div class="feedback-section">
                <h4>Was this workflow helpful?</h4>
                <div class="rating-stars">
                  <button
                    v-for="star in 5"
                    :key="star"
                    class="star-btn"
                    :class="{ filled: rating >= star }"
                    @click="rating = star"
                  >
                    <c-icon :name="rating >= star ? 'cilStar' : 'cilStarOutline'" />
                  </button>
                </div>
                <textarea
                  v-model="feedbackComment"
                  placeholder="Any additional feedback? (optional)"
                  class="feedback-textarea"
                ></textarea>
                <button class="btn-submit-feedback" @click="submitFeedback">
                  Submit Feedback
                </button>
              </div>

              <div class="completion-actions">
                <button class="btn-restart" @click="restartWorkflow">
                  <c-icon name="cilReload" /> Start Over
                </button>
                <button class="btn-close-modal" @click="close">
                  <c-icon name="cilCheckCircle" /> Close
                </button>
              </div>
            </div>
          </div>

          <!-- Overview Mode View -->
          <div v-else-if="currentWorkflow && viewMode === 'overview'" class="overview-view">
            <!-- Description Banner -->
            <div class="overview-banner">
              <div class="banner-icon-overview">
                <c-icon name="cilListNumbered" />
              </div>
              <div class="overview-description">
                <p>{{ currentWorkflow.description }}</p>
              </div>
            </div>

            <div class="overview-grid">
              <!-- Steps Overview -->
              <div class="overview-section">
                <div class="section-title">
                  <c-icon name="cilTask" />
                  <h3>Workflow Steps</h3>
                  <c-badge color="info" class="steps-badge">{{ currentWorkflow.total_steps }} Steps</c-badge>
                </div>
                
                <div class="steps-checklist">
                  <div
                    v-for="(step, index) in currentWorkflow.steps"
                    :key="index"
                    class="step-item-overview"
                    :class="{
                      completed: completedSteps.includes(index),
                      current: currentStep === index,
                    }"
                  >
                    <div class="step-number-overview">
                      <span>{{ index + 1 }}</span>
                    </div>
                    <div class="step-content-overview">
                      <div class="step-title-overview">
                        <strong>{{ step.title }}</strong>
                        <c-badge v-if="completedSteps.includes(index)" color="success" size="sm">
                          <c-icon name="cilCheckCircle" /> Completed
                        </c-badge>
                        <c-badge v-else-if="currentStep === index" color="primary" size="sm">
                          In Progress
                        </c-badge>
                      </div>
                      <p class="step-desc-overview">{{ step.description }}</p>
                      <div v-if="step.notes" class="step-note-overview">
                        <c-icon name="cilLightbulb" />
                        <span>{{ step.notes }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Metadata Sidebar -->
              <div class="overview-sidebar">
                <!-- Workflow Info Card -->
                <div class="info-card-overview">
                  <div class="card-header-overview">
                    <c-icon name="cilInfo" />
                    <h4>Workflow Info</h4>
                  </div>
                  <div class="info-items">
                    <div class="info-item-overview">
                      <div class="info-icon">
                        <c-icon name="cilClock" />
                      </div>
                      <div class="info-text">
                        <span class="info-label">Duration</span>
                        <span class="info-value">{{ currentWorkflow.estimated_time }}</span>
                      </div>
                    </div>
                    <div class="info-item-overview">
                      <div class="info-icon">
                        <c-icon name="cilGauge" />
                      </div>
                      <div class="info-text">
                        <span class="info-label">Difficulty</span>
                        <span class="info-value">
                          <c-badge :color="currentWorkflow.difficulty_badge_color">
                            {{ currentWorkflow.difficulty_level }}
                          </c-badge>
                        </span>
                      </div>
                    </div>
                    <div class="info-item-overview">
                      <div class="info-icon">
                        <c-icon name="cilCheckCircle" />
                      </div>
                      <div class="info-text">
                        <span class="info-label">Success Rate</span>
                        <span class="info-value">{{ currentWorkflow.completion_rate }}%</span>
                      </div>
                    </div>
                    <div v-if="currentWorkflow.average_rating > 0" class="info-item-overview">
                      <div class="info-icon">
                        <c-icon name="cilStar" />
                      </div>
                      <div class="info-text">
                        <span class="info-label">Rating</span>
                        <span class="info-value">{{ currentWorkflow.average_rating }}/5 ⭐</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Quick Actions -->
                <div v-if="currentWorkflow.quick_actions && currentWorkflow.quick_actions.length > 0" class="quick-actions-card">
                  <div class="card-header-overview">
                    <c-icon name="cilBolt" />
                    <h4>Quick Actions</h4>
                  </div>
                  <div class="quick-actions-list">
                    <button
                      v-for="(action, index) in currentWorkflow.quick_actions"
                      :key="index"
                      class="quick-action-btn"
                      @click="executeAction(action)"
                    >
                      <c-icon :name="action.icon || 'cilArrowRight'" />
                      <span>{{ action.label }}</span>
                    </button>
                  </div>
                </div>

                <!-- Video Tutorial -->
                <div v-if="currentWorkflow.video_url" class="video-card">
                  <div class="card-header-overview">
                    <c-icon name="cilMediaPlay" />
                    <h4>Video Tutorial</h4>
                  </div>
                  <a :href="currentWorkflow.video_url" target="_blank" class="video-link">
                    <div class="video-thumbnail">
                      <c-icon name="cilMediaPlay" class="play-icon" />
                    </div>
                    <span>Watch Video Guide</span>
                  </a>
                </div>

                <!-- Start Guided Mode Button -->
                <button class="btn-start-guided" @click="switchToGuidedMode">
                  <c-icon name="cilMediaPlay" /> 
                  <span>Start Guided Mode</span>
                  <c-icon name="cilArrowRight" />
                </button>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="empty-state">
            <c-icon name="cilListNumbered" class="empty-icon" />
            <h3>No Workflows Found</h3>
            <p>Try a different search or browse available workflows.</p>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios';

export default {
  name: 'WorkflowGuide',

  data() {
    return {
      isVisible: false,
      loading: false,
      error: null,
      viewMode: 'guided', // guided, overview
      currentWorkflow: null,
      workflows: [],
      alternatives: [],
      workarounds: [],
      currentStep: -1, // -1 = prerequisites, 0+ = actual steps
      completedSteps: [],
      skippedSteps: [],
      progressId: null,
      sessionId: null,
      showAlternatives: false,
      rating: 0,
      feedbackComment: '',
      retryIntent: null,
    };
  },

  computed: {
    isDarkMode() {
      return this.$store.state.darkMode;
    },

    hasAlternatives() {
      return this.alternatives.length > 0 || this.workarounds.length > 0;
    },

    currentStepData() {
      if (!this.currentWorkflow || this.currentStep < 0) {
        return null;
      }
      return this.currentWorkflow.steps[this.currentStep] || null;
    },

    progressPercentage() {
      if (!this.currentWorkflow) return 0;
      // Calculate based on current step position, not completed steps
      // This ensures the progress bar updates when navigating back and forth
      if (this.currentStep < 0) return 0; // Prerequisites screen
      const progress = ((this.currentStep + 1) / this.currentWorkflow.total_steps) * 100;
      return Math.min(progress, 100); // Cap at 100%
    },
  },

  mounted() {
    // Listen for workflow guide open events
    this.$root.$on('open-workflow-guide', this.handleOpenEvent);
    
    // Generate session ID
    this.sessionId = this.generateSessionId();
  },

  beforeDestroy() {
    this.$root.$off('open-workflow-guide', this.handleOpenEvent);
  },

  methods: {
    /**
     * Open workflow guide with intent or workflow ID
     */
    async open(intentOrId, context = {}) {
      this.isVisible = true;
      this.loading = true;
      this.error = null;

      try {
        if (typeof intentOrId === 'number') {
          // Load specific workflow by ID
          await this.loadWorkflow(intentOrId);
        } else {
          // Suggest workflows based on intent
          await this.suggestWorkflows(intentOrId, context);
        }
      } catch (err) {
        this.error = err.message || 'Failed to load workflows';
        this.retryIntent = { intentOrId, context };
      } finally {
        this.loading = false;
      }
    },

    /**
     * Handle open event from other components
     */
    handleOpenEvent({ intent, workflowId, context }) {
      this.open(workflowId || intent, context);
    },

    /**
     * Suggest workflows based on intent
     */
    async suggestWorkflows(intent, context = {}) {
      const response = await axios.post('/api/ai/workflows/suggest', {
        intent,
        context: {
          ...context,
          page: this.$route.name,
          page_url: this.$route.path,
        },
      });

      if (response.data.success) {
        this.workflows = response.data.data.workflows || [];
        this.alternatives = response.data.data.alternatives || [];
        this.workarounds = response.data.data.workarounds || [];

        // Auto-load first workflow if only one result
        if (this.workflows.length === 1) {
          await this.loadWorkflow(this.workflows[0].id);
        }
      }
    },

    /**
     * Load specific workflow
     */
    async loadWorkflow(workflowId, forceRestart = false) {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get(`/api/ai/workflows/${workflowId}`);

        if (response.data.success) {
          this.currentWorkflow = response.data.data.workflow;
          this.alternatives = response.data.data.alternatives || [];
          this.workarounds = response.data.data.workarounds || [];

          // Always start from the beginning (don't restore saved progress)
          // This prevents the workflow from jumping to a previously saved step
          this.currentStep = -1; // Start at prerequisites
          this.completedSteps = [];
          this.skippedSteps = [];
          this.progressId = null;
        }
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to load workflow';
      } finally {
        this.loading = false;
      }
    },

    /**
     * Start workflow tracking
     */
    async startWorkflow() {
      if (!this.currentWorkflow) return;

      try {
        const response = await axios.post(`/api/ai/workflows/${this.currentWorkflow.id}/start`, {
          session_id: this.sessionId,
          context: {
            page: this.$route.name,
            page_url: this.$route.path,
          },
        });

        if (response.data.success) {
          this.progressId = response.data.data.progress_id;
          this.currentStep = 0;
          this.completedSteps = [];
          this.skippedSteps = [];
        }
      } catch (err) {
        console.error('Failed to start workflow:', err);
      }
    },

    /**
     * Next step
     */
    async nextStep() {
      if (!this.currentWorkflow) return;

      // Mark current step as completed
      if (this.currentStep >= 0 && !this.completedSteps.includes(this.currentStep)) {
        this.completedSteps.push(this.currentStep);
      }

      // Update progress in backend
      if (this.progressId) {
        await this.updateProgress(this.currentStep, { completed: true });
      }

      // Move to next step
      this.currentStep++;
    },

    /**
     * Previous step
     */
    previousStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },

    /**
     * Skip step
     */
    async skipStep() {
      if (!this.skippedSteps.includes(this.currentStep)) {
        this.skippedSteps.push(this.currentStep);
      }

      // Update progress in backend
      if (this.progressId) {
        await this.updateProgress(this.currentStep, { skipped: true });
      }

      this.currentStep++;
    },

    /**
     * Update progress in backend
     */
    async updateProgress(stepIndex, data) {
      if (!this.progressId) return;

      try {
        await axios.put(`/api/ai/workflows/progress/${this.progressId}`, {
          step_index: stepIndex,
          ...data,
        });
      } catch (err) {
        console.error('Failed to update progress:', err);
      }
    },

    /**
     * Submit feedback
     */
    async submitFeedback() {
      if (!this.progressId || this.rating === 0) return;

      try {
        await axios.post(`/api/ai/workflows/progress/${this.progressId}/feedback`, {
          was_helpful: this.rating >= 3,
          rating: this.rating,
          feedback_comment: this.feedbackComment,
        });

        this.$toast.success('Thank you for your feedback!');
      } catch (err) {
        console.error('Failed to submit feedback:', err);
      }
    },

    /**
     * Execute action (navigate, etc.)
     */
    executeAction(action) {
      if (action.type === 'navigate') {
        this.$router.push(action.url);
        this.close();
      } else if (action.type === 'link') {
        window.open(action.url, '_blank');
      } else if (action.type === 'execute') {
        // Custom execution logic
        this.$emit('execute-action', action);
      }
    },

    /**
     * Show workaround details
     */
    showWorkaround(workaround) {
      // You can implement a separate modal or expand inline
      const steps = workaround.workaround_steps.map((s, i) => `${i + 1}. ${s}`).join('\n');
      alert(`Workaround: ${workaround.workaround_description}\n\nSteps:\n${steps}`);
    },

    /**
     * Switch to guided mode
     */
    switchToGuidedMode() {
      this.viewMode = 'guided';
      if (this.currentStep === -1) {
        this.startWorkflow();
      }
    },

    /**
     * Restart workflow
     */
    async restartWorkflow() {
      this.currentStep = -1;
      this.completedSteps = [];
      this.skippedSteps = [];
      this.rating = 0;
      this.feedbackComment = '';
      this.progressId = null;
      
      // Start fresh tracking session
      if (this.currentWorkflow) {
        await this.startWorkflow();
      }
    },

    /**
     * Restore progress from saved state
     */
    restoreProgress(progress) {
      this.progressId = progress.id;
      this.currentStep = progress.current_step;
      this.completedSteps = progress.completed_steps || [];
      this.skippedSteps = progress.skipped_steps || [];
    },

    /**
     * Retry loading
     */
    retry() {
      if (this.retryIntent) {
        this.open(this.retryIntent.intentOrId, this.retryIntent.context);
      }
    },

    /**
     * Open AI Assistant
     */
    openAIAssistant() {
      this.$store.dispatch('aiAssistant/open');
      this.$store.dispatch('aiAssistant/sendMessage', {
        message: `I need help with step ${this.currentStep + 1}: ${this.currentStepData?.title}`,
        context: {
          workflow: this.currentWorkflow?.name,
          step: this.currentStep,
        },
      });
    },

    /**
     * Close modal
     */
    close() {
      this.isVisible = false;
      this.currentWorkflow = null;
      this.workflows = [];
      this.alternatives = [];
      this.workarounds = [];
      this.currentStep = -1;
      this.error = null;
    },

    /**
     * Get workflow icon
     */
    getWorkflowIcon(type = null) {
      const workflowType = type || this.currentWorkflow?.workflow_type;
      switch (workflowType) {
        case 'standard': return 'cilListNumbered';
        case 'workaround': return 'cilWrapText';
        case 'alternative': return 'cilSwapHorizontal';
        case 'shortcut': return 'cilBolt';
        default: return 'cilTask';
      }
    },

    /**
     * Format workflow type
     */
    formatType(type) {
      return type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    /**
     * Format description (support basic markdown)
     */
    formatDescription(text) {
      if (!text) return '';
      return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br>');
    },

    /**
     * Generate session ID
     */
    generateSessionId() {
      return 'workflow_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    },
  },
};
</script>

<style scoped>
/* Base Modal Styles */
.workflow-guide-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  backdrop-filter: blur(4px);
}

.workflow-guide-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.2);
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Dark Mode */
.workflow-guide-modal.dark-mode .workflow-guide-container {
  background: #2a2a2a;
  color: #e0e0e0;
}

/* Header */
.workflow-header {
  padding: 24px 28px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.dark-mode .workflow-header {
  border-bottom-color: #3a3a3a;
}

.header-left {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex: 1;
}

.workflow-icon {
  font-size: 32px;
  opacity: 0.9;
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.workflow-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
}

.workflow-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
}

.time-estimate {
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0.9;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.btn-alternatives,
.btn-close {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-alternatives:hover,
.btn-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* View Mode Switcher */
.view-mode-switcher {
  display: flex;
  gap: 8px;
  padding: 16px 28px;
  border-bottom: 1px solid #e0e0e0;
  background: #f5f5f5;
}

.dark-mode .view-mode-switcher {
  background: #1e1e1e;
  border-bottom-color: #3a3a3a;
}

.view-mode-switcher button {
  flex: 1;
  padding: 10px 16px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
}

.dark-mode .view-mode-switcher button {
  background: #2a2a2a;
  border-color: #3a3a3a;
  color: #e0e0e0;
}

.view-mode-switcher button.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-color: transparent;
}

/* Content Area */
.workflow-content {
  flex: 1;
  overflow-y: auto;
  padding: 28px;
}

/* Loading/Error States */
.loading-state,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e0e0e0;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-icon,
.empty-icon {
  font-size: 64px;
  color: #f44336;
  margin-bottom: 16px;
}

.empty-icon {
  color: #9e9e9e;
}

.btn-retry {
  margin-top: 16px;
  padding: 10px 24px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-retry:hover {
  background: #764ba2;
  transform: translateY(-2px);
}

/* Workflow List */
.workflow-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.workflow-card {
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.dark-mode .workflow-card {
  background: #1e1e1e;
  border-color: #3a3a3a;
}

.workflow-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.2);
  border-color: #667eea;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.card-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.card-description {
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
  line-height: 1.5;
}

.dark-mode .card-description {
  color: #aaa;
}

.card-meta {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #888;
}

.card-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.card-stats {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 16px;
  font-size: 13px;
}

.dark-mode .card-stats {
  border-top-color: #3a3a3a;
}

.stat {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Prerequisites Screen */
.prerequisites-screen {
  max-width: 700px;
  margin: 0 auto;
}

.prerequisites-banner {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 28px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  color: white;
  margin-bottom: 32px;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.25);
}

.banner-icon {
  font-size: 48px;
  opacity: 0.9;
  flex-shrink: 0;
}

.banner-text h3 {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 700;
}

.banner-text p {
  margin: 0;
  font-size: 15px;
  opacity: 0.95;
  line-height: 1.5;
}

.prerequisites-section,
.expected-outcome {
  background: #f9f9f9;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
}

.dark-mode .prerequisites-section,
.dark-mode .expected-outcome {
  background: #1e1e1e;
  border: 1px solid #3a3a3a;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #667eea;
}

.section-header svg {
  font-size: 24px;
  color: #667eea;
}

.section-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.dark-mode .section-header h4 {
  color: #e0e0e0;
}

.prerequisites-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.prerequisite-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border-left: 3px solid #4caf50;
  transition: all 0.2s;
}

.dark-mode .prerequisite-item {
  background: #2a2a2a;
}

.prerequisite-item:hover {
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.prereq-checkbox {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.prereq-checkbox svg {
  font-size: 20px;
  color: #4caf50;
}

.prereq-text {
  flex: 1;
  font-size: 15px;
  line-height: 1.6;
  color: #333;
}

.dark-mode .prereq-text {
  color: #e0e0e0;
}

.outcomes-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.outcome-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border-left: 3px solid #667eea;
  transition: all 0.2s;
}

.dark-mode .outcome-item {
  background: #2a2a2a;
}

.outcome-item:hover {
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.outcome-icon {
  flex-shrink: 0;
  font-size: 18px;
  color: #667eea;
  margin-top: 2px;
}

.outcome-item span {
  flex: 1;
  font-size: 15px;
  line-height: 1.6;
  color: #333;
}

.dark-mode .outcome-item span {
  color: #e0e0e0;
}

.workflow-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 32px;
}

.stat-card {
  background: #f9f9f9;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid #e0e0e0;
  transition: all 0.2s;
}

.dark-mode .stat-card {
  background: #1e1e1e;
  border-color: #3a3a3a;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

.stat-card svg {
  font-size: 32px;
  color: #667eea;
  flex-shrink: 0;
}

.stat-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-label {
  font-size: 12px;
  color: #888;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: #333;
}

.dark-mode .stat-value {
  color: #e0e0e0;
}

.start-button-container {
  text-align: center;
}

.btn-start-workflow {
  padding: 16px 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  transition: all 0.3s;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
}

.btn-start-workflow:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
}

.btn-start-workflow:active {
  transform: translateY(-1px);
}

.btn-start-workflow svg {
  font-size: 20px;
}

/* Step View */
.step-progress {
  margin-bottom: 32px;
}

/* Overview Mode Styles */
.overview-view {
  max-width: 1200px;
  margin: 0 auto;
}

.overview-banner {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 24px;
  color: white;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.25);
}

.banner-icon-overview {
  font-size: 48px;
  opacity: 0.9;
  flex-shrink: 0;
}

.overview-description {
  flex: 1;
}

.overview-description p {
  margin: 0;
  font-size: 16px;
  line-height: 1.6;
  opacity: 0.95;
}

.overview-grid {
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 24px;
  align-items: start;
}

.overview-section {
  background: #f9f9f9;
  border-radius: 16px;
  padding: 24px;
}

.dark-mode .overview-section {
  background: #1e1e1e;
  border: 1px solid #3a3a3a;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #667eea;
}

.section-title svg {
  font-size: 24px;
  color: #667eea;
}

.section-title h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #333;
  flex: 1;
}

.dark-mode .section-title h3 {
  color: #e0e0e0;
}

.steps-badge {
  font-size: 12px;
  font-weight: 600;
}

.steps-checklist {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-item-overview {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  gap: 16px;
  border: 2px solid #e0e0e0;
  transition: all 0.3s;
  position: relative;
}

.dark-mode .step-item-overview {
  background: #2a2a2a;
  border-color: #3a3a3a;
}

.step-item-overview.completed {
  border-color: #4caf50;
  background: #f1f8f4;
}

.dark-mode .step-item-overview.completed {
  background: #1a2e1f;
  border-color: #4caf50;
}

.step-item-overview.current {
  border-color: #667eea;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.2);
}

.step-item-overview:hover {
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.step-number-overview {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.step-item-overview.completed .step-number-overview {
  background: #4caf50;
}

.step-content-overview {
  flex: 1;
  min-width: 0;
}

.step-title-overview {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.step-title-overview strong {
  font-size: 16px;
  color: #333;
  line-height: 1.4;
}

.dark-mode .step-title-overview strong {
  color: #e0e0e0;
}

.step-desc-overview {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #666;
  line-height: 1.6;
}

.dark-mode .step-desc-overview {
  color: #aaa;
}

.step-note-overview {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #fff9e6;
  border-radius: 6px;
  border-left: 3px solid #ffc107;
  font-size: 13px;
  color: #856404;
}

.dark-mode .step-note-overview {
  background: #3a3200;
  color: #ffc107;
}

.step-note-overview svg {
  color: #ffc107;
  font-size: 16px;
  flex-shrink: 0;
}

/* Overview Sidebar */
.overview-sidebar {
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: sticky;
  top: 20px;
}

.info-card-overview,
.quick-actions-card,
.video-card {
  background: #f9f9f9;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e0e0e0;
}

.dark-mode .info-card-overview,
.dark-mode .quick-actions-card,
.dark-mode .video-card {
  background: #1e1e1e;
  border-color: #3a3a3a;
}

.card-header-overview {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #667eea;
}

.card-header-overview svg {
  font-size: 20px;
  color: #667eea;
}

.card-header-overview h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #333;
}

.dark-mode .card-header-overview h4 {
  color: #e0e0e0;
}

.info-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-item-overview {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  transition: all 0.2s;
}

.dark-mode .info-item-overview {
  background: #2a2a2a;
}

.info-item-overview:hover {
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.info-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.info-icon svg {
  font-size: 18px;
  color: white;
}

.info-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-label {
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 15px;
  font-weight: 700;
  color: #333;
}

.dark-mode .info-value {
  color: #e0e0e0;
}

.quick-actions-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.quick-action-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
  color: #333;
}

.dark-mode .quick-action-btn {
  background: #2a2a2a;
  border-color: #3a3a3a;
  color: #e0e0e0;
}

.quick-action-btn:hover {
  background: #667eea;
  color: white;
  border-color: #667eea;
  transform: translateX(4px);
}

.quick-action-btn svg {
  font-size: 18px;
}

.video-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  text-decoration: none;
  color: #333;
  transition: all 0.2s;
  border: 2px solid #e0e0e0;
}

.dark-mode .video-link {
  background: #2a2a2a;
  color: #e0e0e0;
  border-color: #3a3a3a;
}

.video-link:hover {
  border-color: #667eea;
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.video-thumbnail {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.play-icon {
  font-size: 28px;
  color: white;
}

.video-link span {
  font-weight: 600;
  font-size: 14px;
}

.btn-start-guided {
  width: 100%;
  padding: 16px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.3s;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
}

.btn-start-guided:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
}

.btn-start-guided svg {
  font-size: 20px;
}

.progress-bar {
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.step-content {
  background: #f9f9f9;
  border-radius: 12px;
  padding: 28px;
  margin-bottom: 24px;
}

.dark-mode .step-content {
  background: #1e1e1e;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.step-number {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
}

.step-description {
  line-height: 1.6;
  margin-bottom: 20px;
}

.step-action {
  margin: 20px 0;
}

.btn-action {
  padding: 12px 24px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-action:hover {
  background: #764ba2;
  transform: translateY(-2px);
}

.step-notes {
  background: #fff9e6;
  border-left: 4px solid #ffc107;
  padding: 12px 16px;
  border-radius: 4px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-top: 20px;
}

.dark-mode .step-notes {
  background: #3a3200;
  border-left-color: #ffc107;
}

.step-navigation {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.btn-prev,
.btn-next,
.btn-skip {
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-prev,
.btn-skip {
  background: white;
  border: 1px solid #ddd;
  color: #333;
}

.dark-mode .btn-prev,
.dark-mode .btn-skip {
  background: #2a2a2a;
  border-color: #3a3a3a;
  color: #e0e0e0;
}

.btn-next {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
}

.btn-prev:disabled,
.btn-next:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-need-help {
  margin-top: 20px;
  padding: 10px 20px;
  background: #f44336;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* Completion Screen */
.completion-screen {
  text-align: center;
  padding: 40px 20px;
}

.completion-icon {
  font-size: 80px;
  color: #4caf50;
  margin-bottom: 24px;
}

.feedback-section {
  margin: 32px 0;
  padding: 24px;
  background: #f9f9f9;
  border-radius: 12px;
}

.dark-mode .feedback-section {
  background: #1e1e1e;
}

.rating-stars {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin: 16px 0;
}

.star-btn {
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
  color: #ddd;
  transition: all 0.2s;
}

.star-btn.filled {
  color: #ffc107;
}

.feedback-textarea {
  width: 100%;
  min-height: 80px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-top: 16px;
  font-family: inherit;
  resize: vertical;
}

.dark-mode .feedback-textarea {
  background: #2a2a2a;
  border-color: #3a3a3a;
  color: #e0e0e0;
}

.btn-submit-feedback {
  margin-top: 16px;
  padding: 10px 24px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.completion-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
}

/* Animations */
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: all 0.3s ease;
}

.fade-scale-enter,
.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

/* Responsive */
@media (max-width: 768px) {
  .workflow-guide-container {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }

  .workflow-grid {
    grid-template-columns: 1fr;
  }

  .step-navigation {
    flex-direction: column;
  }

  /* Prerequisites Mobile Responsive */
  .prerequisites-banner {
    flex-direction: column;
    text-align: center;
    padding: 24px 20px;
  }

  .banner-text h3 {
    font-size: 20px;
  }

  .banner-text p {
    font-size: 14px;
  }

  .workflow-stats {
    grid-template-columns: 1fr;
  }

  .stat-card {
    justify-content: center;
  }

  .btn-start-workflow {
    width: 100%;
    justify-content: center;
  }

  .prerequisites-section,
  .expected-outcome {
    padding: 20px;
  }

  /* Overview Mobile Responsive */
  .overview-grid {
    grid-template-columns: 1fr;
  }

  .overview-banner {
    flex-direction: column;
    text-align: center;
    padding: 24px 20px;
  }

  .banner-icon-overview {
    font-size: 36px;
  }

  .overview-description p {
    font-size: 14px;
  }

  .overview-sidebar {
    position: static;
  }

  .section-title {
    flex-wrap: wrap;
  }

  .step-item-overview {
    padding: 16px;
  }

  .step-number-overview {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }

  .btn-start-guided {
    padding: 14px 20px;
    font-size: 15px;
  }
}
</style>
